import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sparkles, Copy, Download, RefreshCw, Image, Quote,
  Twitter, Facebook, Linkedin, Instagram, Check, Loader2,
  Home, DollarSign, MapPin, Star, Heart
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const WinnerStoryGenerator = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState('');
  
  const [inputs, setInputs] = useState({
    winnerName: '',
    city: '',
    province: '',
    prizeType: '',
    prizeValue: '',
    projectDescription: '',
    winnerQuote: '',
    sponsorName: '',
    beforeDescription: '',
    afterDescription: ''
  });

  const [generatedContent, setGeneratedContent] = useState({
    headline: '',
    shortStory: '',
    longStory: '',
    socialPost: '',
    twitterPost: '',
    pressRelease: ''
  });

  const prizeTypes = [
    'Kitchen Renovation',
    'Bathroom Remodel', 
    'Landscaping Package',
    'Roof Replacement',
    'HVAC System',
    'Window Replacement',
    'Basement Finishing',
    'Deck/Patio Build',
    'Flooring Installation',
    'Painting Package'
  ];

  const generateStories = async () => {
    if (!inputs.winnerName || !inputs.prizeType || !inputs.prizeValue) {
      toast.error('Please fill in winner name, prize type, and value');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/api/admin/generate-winner-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inputs)
      });

      if (!response.ok) {
        throw new Error('Failed to generate stories');
      }

      const data = await response.json();
      setGeneratedContent(data);
      toast.success('Stories generated successfully!');
    } catch (error) {
      // Fallback to local generation if API fails
      generateLocalStories();
    } finally {
      setLoading(false);
    }
  };

  const generateLocalStories = () => {
    const { winnerName, city, province, prizeType, prizeValue, projectDescription, winnerQuote, sponsorName } = inputs;
    const firstName = winnerName.split(' ')[0];
    const location = city && province ? `${city}, ${province}` : city || 'their neighborhood';
    const valueFormatted = `$${parseInt(prizeValue).toLocaleString()}`;

    setGeneratedContent({
      headline: `${firstName} from ${location} Wins ${valueFormatted} ${prizeType}!`,
      
      shortStory: `Congratulations to ${winnerName} from ${location}! They've won a ${valueFormatted} ${prizeType.toLowerCase()} through Spinser's home improvement giveaway program. ${winnerQuote ? `"${winnerQuote}" - ${firstName}` : ''} ${sponsorName ? `Thank you to ${sponsorName} for sponsoring this incredible prize!` : ''}`,
      
      longStory: `We're thrilled to announce ${winnerName} from ${location} as our latest Spinser winner!\n\n${firstName} entered our free ${prizeType.toLowerCase()} giveaway and was selected from hundreds of local homeowners. ${projectDescription ? `Their project: ${projectDescription}` : ''}\n\n${winnerQuote ? `When we called ${firstName} with the news, they said: "${winnerQuote}"` : ''}\n\nThe ${valueFormatted} prize includes professional installation by our verified contractor network. ${sponsorName ? `Special thanks to ${sponsorName} for making this transformation possible.` : ''}\n\nWant to be our next winner? Enter free at tryspinser.com - no purchase necessary!`,
      
      socialPost: `🎉 WINNER ANNOUNCEMENT! 🎉\n\nCongrats to ${firstName} from ${location} who just won a ${valueFormatted} ${prizeType}! 🏠✨\n\n${winnerQuote ? `"${winnerQuote}" - ${firstName}` : ''}\n\nYOU could be next! Enter FREE at tryspinser.com 👉\n\n#HomeImprovement #Giveaway #Winner #${city?.replace(/\s/g, '') || 'HomeUpgrade'} #Spinser`,
      
      twitterPost: `🎉 ${firstName} from ${location} just won a ${valueFormatted} ${prizeType}!\n\n${winnerQuote ? `"${winnerQuote}"` : 'Another happy Spinser winner!'}\n\nEnter FREE: tryspinser.com\n\n#HomeGiveaway #Winner`,
      
      pressRelease: `FOR IMMEDIATE RELEASE\n\n${location.toUpperCase()} RESIDENT WINS ${valueFormatted} HOME IMPROVEMENT GIVEAWAY\n\nSpinser Awards ${prizeType} to Local Homeowner\n\n${location} - ${winnerName}, a resident of ${location}, has been selected as the winner of Spinser's ${prizeType.toLowerCase()} giveaway, valued at ${valueFormatted}.\n\nThe prize was awarded through Spinser's innovative platform that connects homeowners with local sponsors offering free home improvement projects. ${sponsorName ? `The giveaway was sponsored by ${sponsorName}.` : ''}\n\n${winnerQuote ? `"${winnerQuote}" said ${winnerName}.` : ''}\n\nSpinser offers homeowners the chance to win valuable home improvements at no cost. Interested homeowners can enter at tryspinser.com.\n\n###\n\nAbout Spinser:\nSpinser connects homeowners with local businesses through sponsored home improvement giveaways. No purchase necessary to enter.\n\nMedia Contact: press@tryspinser.com`
    });
    
    toast.success('Stories generated!');
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
    toast.success('Copied to clipboard!');
  };

  const ContentCard = ({ title, content, type, icon: Icon }) => (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-neutral-500" />
          <span className="font-medium text-neutral-700">{title}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => copyToClipboard(content, type)}
          className="h-8"
        >
          {copied === type ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </Button>
      </div>
      <div className="p-4">
        <p className="text-neutral-700 whitespace-pre-wrap text-sm">{content}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
            Winner Story Generator
          </h1>
          <p className="text-neutral-500 mt-1">Create compelling winner announcements for social media, press, and more</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h2 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FF3B30]" />
            Winner Details
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Winner Name *</Label>
                <Input
                  placeholder="Sarah Mitchell"
                  value={inputs.winnerName}
                  onChange={(e) => setInputs({...inputs, winnerName: e.target.value})}
                  className="mt-1"
                  data-testid="winner-name-input"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  placeholder="Toronto"
                  value={inputs.city}
                  onChange={(e) => setInputs({...inputs, city: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Province/State</Label>
                <Input
                  placeholder="Ontario"
                  value={inputs.province}
                  onChange={(e) => setInputs({...inputs, province: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Prize Value *</Label>
                <Input
                  type="number"
                  placeholder="8500"
                  value={inputs.prizeValue}
                  onChange={(e) => setInputs({...inputs, prizeValue: e.target.value})}
                  className="mt-1"
                  data-testid="prize-value-input"
                />
              </div>
            </div>

            <div>
              <Label>Prize Type *</Label>
              <Select 
                value={inputs.prizeType} 
                onValueChange={(v) => setInputs({...inputs, prizeType: v})}
              >
                <SelectTrigger className="mt-1" data-testid="prize-type-select">
                  <SelectValue placeholder="Select prize type" />
                </SelectTrigger>
                <SelectContent>
                  {prizeTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Project Description</Label>
              <Textarea
                placeholder="Describe what the project involves..."
                value={inputs.projectDescription}
                onChange={(e) => setInputs({...inputs, projectDescription: e.target.value})}
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>Winner Quote</Label>
              <Textarea
                placeholder="I couldn't believe I won! This is going to transform our home..."
                value={inputs.winnerQuote}
                onChange={(e) => setInputs({...inputs, winnerQuote: e.target.value})}
                className="mt-1"
                rows={2}
                data-testid="winner-quote-input"
              />
            </div>

            <div>
              <Label>Sponsor Name (optional)</Label>
              <Input
                placeholder="ABC Realty"
                value={inputs.sponsorName}
                onChange={(e) => setInputs({...inputs, sponsorName: e.target.value})}
                className="mt-1"
              />
            </div>

            <Button
              onClick={generateStories}
              disabled={loading}
              className="w-full h-11 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl"
              data-testid="generate-stories-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Stories
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Generated Content */}
        <div className="space-y-4">
          {generatedContent.headline ? (
            <>
              <ContentCard 
                title="Headline" 
                content={generatedContent.headline}
                type="headline"
                icon={Star}
              />
              <ContentCard 
                title="Short Story (Email/Website)" 
                content={generatedContent.shortStory}
                type="short"
                icon={Quote}
              />
              <ContentCard 
                title="Social Media Post" 
                content={generatedContent.socialPost}
                type="social"
                icon={Instagram}
              />
              <ContentCard 
                title="Twitter/X Post" 
                content={generatedContent.twitterPost}
                type="twitter"
                icon={Twitter}
              />
              <ContentCard 
                title="Long Story (Blog/Press)" 
                content={generatedContent.longStory}
                type="long"
                icon={Home}
              />
              <ContentCard 
                title="Press Release" 
                content={generatedContent.pressRelease}
                type="press"
                icon={DollarSign}
              />
            </>
          ) : (
            <div className="bg-neutral-50 rounded-xl border border-neutral-200 p-12 text-center">
              <Sparkles className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500">Fill in the winner details and click "Generate Stories" to create compelling content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinnerStoryGenerator;
